import { computed, reactive, ref } from "vue";
import { t } from "#ce/shared/components-i18n";

/**
 * Owns the target and the pane it opens on.
 *
 * The target alone chooses the pane: a record opens the preview, a bare url the url field, and
 * nothing the search. `override` is the one pane a click can pin on top of that, and `cancel`
 * releases it.
 *
 * @param {Object} options
 * @param {{url?: String, id?: String, type?: String, sparkleUrl?: String}} options.initial
 * @param {() => String} options.targetName - Name the mode links are phrased around.
 * @param {() => Boolean} options.hasUrlField - Whether the surrounding form renders a url field.
 * @returns {{
 *   target: {url: String, id: String, type: String, sparkleUrl: String},
 *   mode: import("vue").ComputedRef<"preview"|"url"|"search">,
 *   actions: import("vue").ComputedRef<Array<{key: String, label: String}>>,
 *   applyTarget: Function,
 *   showSearch: Function,
 *   showCustomUrl: Function,
 *   cancel: Function,
 * }}
 */
export function useTargetSelection({ initial, targetName, hasUrlField }) {
  const target = reactive({
    url: initial.url ?? "",
    id: initial.id ?? "",
    type: initial.type ?? "",
    sparkleUrl: initial.sparkleUrl ?? "",
  });

  const override = ref(null);

  const targetPane = computed(() => {
    if (target.sparkleUrl || target.id) {
      return "preview";
    }
    if (target.url) {
      return "url";
    }

    return "search";
  });

  const mode = computed(() => override.value ?? targetPane.value);

  const actions = computed(() => {
    const list = [];

    if (mode.value === "preview") {
      list.push({ key: "change", label: t("target_selector_change") });
      list.push({ key: "remove", label: t("target_selector_remove") });

      return list;
    }

    if (mode.value === "search" && hasUrlField()) {
      list.push({ key: "custom", label: t("target_selector_custom", { name: targetName() }) });
    }

    if (mode.value === "url") {
      list.push({ key: "choose", label: t("target_selector_choose", { name: targetName() }) });
    }

    if (override.value && targetPane.value !== override.value) {
      list.push({ key: "cancel", label: t("target_selector_use_current") });
    }

    return list;
  });

  function applyTarget(next) {
    target.id = next.id ?? "";
    target.type = next.type ?? "";
    target.sparkleUrl = next.sparkleUrl ?? "";
    // The search result carries the record's admin preview path. Consumers read the url column in
    // preference to the record, so a picked record leaves it empty.
    target.url = target.id || target.sparkleUrl ? "" : (next.url ?? "");
    override.value = null;
  }

  function showSearch() {
    override.value = "search";
  }

  function showCustomUrl() {
    override.value = "url";
  }

  function cancel() {
    override.value = null;
  }

  return { target, mode, actions, applyTarget, showSearch, showCustomUrl, cancel };
}
